import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, TrendingDown, TrendingUp, Minus } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatMonto } from '../utils/format';
import BotonAyuda from '../components/BotonAyuda';

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];
const DIAS_SEMANA = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá', 'Do'];
const OPCIONES_DIAS = [30, 60, 90];

function pad(n) {
  return String(n).padStart(2, '0');
}

function diasEnMes(year, mes) {
  return new Date(year, mes + 1, 0).getDate();
}

function primerDiaSemana(year, mes) {
  return (new Date(year, mes, 1).getDay() + 6) % 7; // 0 = lunes
}

function sumarDias(fecha, delta) {
  const [y, m, d] = fecha.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + delta);
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
}

function formatFechaCorta(fecha) {
  const [y, m, d] = fecha.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('es', { day: 'numeric', month: 'short' });
}

// Misma escala de intensidad que el Calendario 1 de Extrusión, en ámbar
// para distinguirlo visualmente de la producción real (teal).
function colorCelda(total, max) {
  if (total <= 0) return 'bg-slate-50/40';
  const intensidad = Math.min(1, total / max);
  if (intensidad < 0.25) return 'bg-rose-50';
  if (intensidad < 0.5) return 'bg-rose-100';
  return 'bg-rose-200';
}

const ETIQUETA_ESTADO = {
  no_trabajado: 'no trabajado',
  sin_cargar: 'sin cargar',
  cargado: 'cargado, bajo el promedio'
};

function agruparPorFecha(dias) {
  const mapa = {};
  dias.forEach((d) => {
    const fecha = String(d.fecha_produccion).slice(0, 10);
    if (!mapa[fecha]) mapa[fecha] = { total: 0, diurno: null, nocturno: null, estadoDiurno: null, estadoNocturno: null };
    mapa[fecha].total += d.deficit_kg;
    mapa[fecha][d.turno] = d.deficit_kg;
    mapa[fecha][d.turno === 'diurno' ? 'estadoDiurno' : 'estadoNocturno'] = d.estado;
  });
  return mapa;
}

function FilaTendencia({ turno, datos }) {
  if (!datos) return null;
  const { promedioSeleccionado, promedio7d, diferencia } = datos;
  const subiendo = diferencia > 0.5;
  const bajando = diferencia < -0.5;
  const Icono = subiendo ? TrendingUp : bajando ? TrendingDown : Minus;
  const color = subiendo ? 'text-teal-600' : bajando ? 'text-red-500' : 'text-slate-400';
  return (
    <div className="flex items-center gap-2 text-xs flex-wrap">
      <span className="capitalize text-slate-500 w-16 flex-shrink-0">{turno}</span>
      <span className="text-slate-600 tabular-nums">{formatMonto(Math.round(promedio7d))} kg (7d)</span>
      <span className="text-slate-300">vs</span>
      <span className="text-slate-600 tabular-nums">{formatMonto(Math.round(promedioSeleccionado))} kg (sel.)</span>
      <span className={`flex items-center gap-0.5 font-semibold ${color}`}>
        <Icono size={13} />
        {diferencia >= 0 ? '+' : ''}
        {formatMonto(Math.round(diferencia))} kg
      </span>
    </div>
  );
}

export default function ProduccionEstimaciones() {
  const { empresaActiva, modulosActivos } = useAuth();
  const hoy = new Date();
  const [mesVista, setMesVista] = useState({ year: hoy.getFullYear(), mes: hoy.getMonth() });
  const [dias, setDias] = useState(30);
  const [celdaAbierta, setCeldaAbierta] = useState(null);
  const [datos, setDatos] = useState({ dias: [], totalDeficitKg: 0, tendenciaPorTurno: {} });
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');

  const rangoMesCeldas = useMemo(() => {
    const offset = primerDiaSemana(mesVista.year, mesVista.mes);
    const totalDiasMes = diasEnMes(mesVista.year, mesVista.mes);
    const primerDiaMes = `${mesVista.year}-${pad(mesVista.mes + 1)}-01`;
    const ultimoDiaMes = `${mesVista.year}-${pad(mesVista.mes + 1)}-${pad(totalDiasMes)}`;
    const sobrante = (7 - ((offset + totalDiasMes) % 7)) % 7;
    return { desde: sumarDias(primerDiaMes, -offset), hasta: sumarDias(ultimoDiaMes, sobrante) };
  }, [mesVista]);

  useEffect(() => {
    if (!empresaActiva) return;
    setCargando(true);
    setError('');
    api
      .get('/produccion/estimacion', { params: { ...rangoMesCeldas, dias } })
      .then(({ data }) => setDatos(data))
      .catch((err) => setError(err.response?.data?.error || 'No se pudo cargar la estimación'))
      .finally(() => setCargando(false));
  }, [empresaActiva, rangoMesCeldas, dias]);

  const datosPorFecha = useMemo(() => agruparPorFecha(datos.dias), [datos.dias]);

  const { semanas, maxDia } = useMemo(() => {
    const offset = primerDiaSemana(mesVista.year, mesVista.mes);
    const totalDiasMes = diasEnMes(mesVista.year, mesVista.mes);
    const primerDiaMes = `${mesVista.year}-${pad(mesVista.mes + 1)}-01`;

    const celdas = [];
    for (let i = offset; i > 0; i--) celdas.push({ fecha: sumarDias(primerDiaMes, -i), enMes: false });
    for (let dia = 1; dia <= totalDiasMes; dia++) {
      celdas.push({ fecha: `${mesVista.year}-${pad(mesVista.mes + 1)}-${pad(dia)}`, enMes: true });
    }
    const sobrante = (7 - (celdas.length % 7)) % 7;
    const ultimaFecha = celdas[celdas.length - 1].fecha;
    for (let i = 1; i <= sobrante; i++) celdas.push({ fecha: sumarDias(ultimaFecha, i), enMes: false });

    const filas = [];
    for (let i = 0; i < celdas.length; i += 7) {
      const semana = celdas.slice(i, i + 7).map((c) => ({
        ...c,
        ...(datosPorFecha[c.fecha] || {
          total: 0,
          diurno: null,
          nocturno: null,
          estadoDiurno: null,
          estadoNocturno: null
        })
      }));
      const totalSemana = semana.reduce((acc, c) => acc + c.total, 0);
      filas.push({ dias: semana, total: totalSemana });
    }

    const maxDiaCalc = Math.max(1, ...filas.flatMap((f) => f.dias.map((d) => d.total)));
    return { semanas: filas, maxDia: maxDiaCalc };
  }, [mesVista, datosPorFecha]);

  function cambiarMes(delta) {
    setCeldaAbierta(null);
    setMesVista(({ year, mes }) => {
      const total = year * 12 + mes + delta;
      return { year: Math.floor(total / 12), mes: ((total % 12) + 12) % 12 };
    });
  }

  if (!modulosActivos?.produccion) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-8 text-center text-slate-500">
        El módulo de Producción no está habilitado para esta empresa.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-slate-800">Estimaciones<BotonAyuda /></h1>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl shadow-sm p-5">
          <p className="text-sm text-slate-500 mb-1">
            Kg por debajo del promedio · {MESES[mesVista.mes]} {mesVista.year}
          </p>
          <p className="text-2xl font-bold text-slate-800 tabular-nums">
            {cargando ? '...' : `${formatMonto(Math.round(datos.totalDeficitKg))} kg`}
          </p>
          <p className="text-xs text-slate-400 mt-1">
            Turnos no trabajados, sin cargar, o cargados con menos del 80% del promedio
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-5 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm text-slate-500">Tendencia: últimos 7 días vs.</p>
            <div className="flex rounded-md border overflow-hidden text-xs flex-shrink-0">
              {OPCIONES_DIAS.map((op) => (
                <button
                  key={op}
                  type="button"
                  onClick={() => setDias(op)}
                  className={`px-2.5 py-1 font-medium transition-colors ${
                    dias === op ? 'bg-teal-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {op}d
                </button>
              ))}
            </div>
          </div>
          <FilaTendencia turno="diurno" datos={datos.tendenciaPorTurno?.diurno} />
          <FilaTendencia turno="nocturno" datos={datos.tendenciaPorTurno?.nocturno} />
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-5">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => cambiarMes(-1)}
            className="p-2 rounded-md text-slate-500 hover:bg-slate-100 transition-colors"
            title="Mes anterior"
          >
            <ChevronLeft size={18} />
          </button>
          <p className="font-medium text-slate-700 flex items-center gap-2">
            <TrendingDown size={16} className="text-rose-600" />
            {MESES[mesVista.mes]} {mesVista.year}
          </p>
          <button
            onClick={() => cambiarMes(1)}
            className="p-2 rounded-md text-slate-500 hover:bg-slate-100 transition-colors"
            title="Mes siguiente"
          >
            <ChevronRight size={18} />
          </button>
        </div>

        {!cargando && !error && datos.dias.length === 0 && (
          <p className="text-sm text-slate-400 mb-3">
            Ningún turno estuvo por debajo del promedio en {MESES[mesVista.mes]} {mesVista.year}.
          </p>
        )}

        <div className="grid grid-cols-8 gap-2 mb-2">
          {DIAS_SEMANA.map((d) => (
            <div key={d} className="text-center text-sm font-semibold text-slate-400 py-1">{d}</div>
          ))}
          <div className="text-center text-sm font-semibold text-slate-400 py-1">Total sem.</div>
        </div>

        {semanas.map((fila, idx) => (
          <div key={idx} className="grid grid-cols-8 gap-2 mb-2">
            {fila.dias.map((c) => (
              <div key={c.fecha} className="relative">
                <button
                  type="button"
                  onClick={() => c.enMes && c.total > 0 && setCeldaAbierta(celdaAbierta === c.fecha ? null : c.fecha)}
                  className={`w-full rounded-lg border p-2 min-h-[60px] flex flex-col justify-between text-left
                    ${c.enMes ? colorCelda(c.total, maxDia) : 'bg-slate-50/50 opacity-50 cursor-default'}
                    ${c.enMes ? 'border-slate-100' : 'border-slate-50'}
                    ${celdaAbierta === c.fecha ? 'ring-1 ring-rose-500' : ''}`}
                >
                  <span className={`text-sm font-semibold ${c.enMes ? 'text-slate-600' : 'text-slate-400'}`}>
                    {Number(c.fecha.slice(-2))}
                  </span>
                  {c.total > 0 && (
                    <span className="text-xs font-medium tabular-nums self-end text-rose-700">
                      {formatMonto(Math.round(c.total))}
                    </span>
                  )}
                </button>

                {celdaAbierta === c.fecha && (
                  <div className="absolute z-20 top-full left-0 mt-1 w-56 rounded-lg border border-slate-200 bg-white p-3 shadow-lg text-xs space-y-2">
                    <p className="font-semibold text-slate-700">{formatFechaCorta(c.fecha)}</p>
                    {['diurno', 'nocturno']
                      .filter((t) => c[t] != null)
                      .map((turno) => (
                        <div key={turno}>
                          <p className="text-slate-500 capitalize">
                            {turno} · {ETIQUETA_ESTADO[turno === 'diurno' ? c.estadoDiurno : c.estadoNocturno]}
                          </p>
                          <p className="text-slate-800 font-medium tabular-nums">
                            {formatMonto(Math.round(c[turno]))} kg por debajo del promedio
                          </p>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            ))}
            <div className="rounded-lg bg-rose-600 shadow-sm p-2 min-h-[60px] flex flex-col justify-center items-center">
              <span className="text-xs text-rose-100 font-semibold">Total</span>
              <span className="text-sm font-bold text-white tabular-nums">{formatMonto(Math.round(fila.total))}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
